import { Box, Flex, IconButton, VStack } from '@chakra-ui/react';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { useCallback, useEffect, useRef, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';

import hussamImg from 'assets/event-concert-hussam.webp';
import zyoonImg from 'assets/event-concert-zyoon.webp';
import zawraaImg from 'assets/event-football-zawraa.webp';
import shkoonImg from 'assets/event-concert-shkoon.webp';
import symphonyImg from 'assets/event-symphony.webp';

import { EventCard, type EventCardVariant } from '../EventCard/EventCard';
import { SectionHeader } from '../SectionHeader/SectionHeader';

/** About V2 sections share the Thmanyah type ramp (see {@link HeroSection}). */
const FONT_FAMILY = "'Thmanyah Sans', 'Noto Sans Arabic', sans-serif";

interface Msg {
  id: string;
  defaultMessage: string;
}

interface EventItem {
  id: string;
  image: string;
  variant?: EventCardVariant;
  category: Msg;
  title: Msg;
  date?: Msg;
  venue?: Msg;
  /** Popularity/attendance badge: an optional label plus a locale-agnostic count ("+8K"). */
  badge?: { label?: Msg; count: string };
  /** Coral highlight pill — featured only. */
  highlight?: Msg;
}

// The first card is the featured (larger) variant, the rest are standard.
const EVENTS: EventItem[] = [
  {
    id: 'hussam',
    image: hussamImg,
    variant: 'featured',
    category: { id: 'about.events.cat.concert', defaultMessage: 'Concert' },
    highlight: { id: 'about.events.item.hussam.highlight', defaultMessage: 'Biggest event' },
    badge: {
      label: { id: 'about.events.attendance', defaultMessage: 'Attendance' },
      count: '+3K',
    },
    title: {
      id: 'about.events.item.hussam.title',
      defaultMessage: 'A night with Hussam Al-Rassam',
    },
    date: { id: 'about.events.date', defaultMessage: '29 May 2026' },
    venue: { id: 'about.events.item.hussam.venue', defaultMessage: 'Nakheel Baghdad' },
  },
  // Standard cards stack two per column; order matches the design grid — the column
  // nearest the featured card is نادي الزوراء / السمفونية, then ناصيف زيتون / Shkoon.
  {
    id: 'zawraa',
    image: zawraaImg,
    category: { id: 'about.events.cat.sport', defaultMessage: 'Sport' },
    badge: { count: '+30K' },
    title: { id: 'about.events.item.zawraa.title', defaultMessage: 'Al-Zawraa club matches' },
    venue: { id: 'about.events.item.zawraa.venue', defaultMessage: 'Al-Zawraa club stadium' },
  },
  {
    id: 'symphony',
    image: symphonyImg,
    category: { id: 'about.events.cat.concertShort', defaultMessage: 'Concert' },
    badge: { count: '+1K' },
    title: {
      id: 'about.events.item.symphony.title',
      defaultMessage: 'Iraqi National Symphony Orchestra',
    },
    venue: { id: 'about.events.item.symphony.venue', defaultMessage: 'National Theatre' },
    date: { id: 'about.events.item.symphony.date', defaultMessage: '16 May 2026' },
  },
  {
    id: 'zyoon',
    image: zyoonImg,
    category: { id: 'about.events.cat.concertShort', defaultMessage: 'Concert' },
    badge: { count: '+3K' },
    title: { id: 'about.events.item.zyoon.title', defaultMessage: 'A night with Naseef Zaytoun' },
    date: { id: 'about.events.item.zyoon.date', defaultMessage: '29 May 2026' },
    venue: { id: 'about.events.item.zyoon.venue', defaultMessage: 'Baghdad' },
  },
  {
    id: 'shkoon',
    image: shkoonImg,
    category: { id: 'about.events.cat.concertShort', defaultMessage: 'Concert' },
    badge: { count: '+1K' },
    title: { id: 'about.events.item.shkoon.title', defaultMessage: 'Shkoon band concert' },
    date: { id: 'about.events.item.shkoon.date', defaultMessage: '11 December 2025' },
    venue: { id: 'about.events.item.shkoon.venue', defaultMessage: 'Erbil' },
  },
];

/**
 * About page Events carousel (TZ-388): the "من فعالياتنا" header (coral eyebrow, dark
 * title "لحظات لا تُنسى." and a muted subtitle) over a horizontal, touch-swipeable
 * carousel of {@link EventCard}s — one featured (large) card plus standard cards.
 *
 * Prev/next arrows scroll the track and disable at the ends. RTL: the track scrolls
 * right-to-left, the coral "next" (advance) arrow points left and the outlined "prev"
 * arrow points right, and disabled state is derived from the RTL-aware scroll position.
 */
export const EventsCarouselSection = () => {
  const intl = useIntl();
  const trackRef = useRef<HTMLDivElement>(null);
  const [canPrev, setCanPrev] = useState(false);
  const [canNext, setCanNext] = useState(false);

  const updateArrows = useCallback(() => {
    const track = trackRef.current;
    if (!track) return;
    const maxScroll = track.scrollWidth - track.clientWidth;
    // RTL scrollLeft is 0 at the right edge (start) and negative towards the left
    // (end) in modern browsers; abs() normalises that to a 0..maxScroll distance.
    const scrolled = Math.abs(track.scrollLeft);
    const hasOverflow = maxScroll > 1;
    setCanPrev(hasOverflow && scrolled > 1);
    setCanNext(hasOverflow && scrolled < maxScroll - 1);
  }, []);

  useEffect(() => {
    const track = trackRef.current;
    if (!track) return;
    updateArrows();
    track.addEventListener('scroll', updateArrows, { passive: true });
    window.addEventListener('resize', updateArrows);
    return () => {
      track.removeEventListener('scroll', updateArrows);
      window.removeEventListener('resize', updateArrows);
    };
  }, [updateArrows]);

  const scrollByDir = (direction: 'prev' | 'next') => {
    const track = trackRef.current;
    if (!track) return;
    const amount = track.clientWidth * 0.8;
    // RTL: advancing ("next") moves content left → scrollLeft decreases.
    track.scrollBy({ left: direction === 'next' ? -amount : amount, behavior: 'smooth' });
  };

  const renderCard = (item: EventItem) => (
    <EventCard
      image={item.image}
      variant={item.variant}
      badge={
        item.badge && (
          <>
            {item.badge.label && (
              <>
                <FormattedMessage
                  id={item.badge.label.id}
                  defaultMessage={item.badge.label.defaultMessage}
                />{' '}
              </>
            )}
            {/* LTR span keeps the leading "+" on the left (e.g. "+10K"). */}
            <Box as='span' dir='ltr'>
              {item.badge.count}
            </Box>
          </>
        )
      }
      highlight={
        item.highlight && (
          <FormattedMessage id={item.highlight.id} defaultMessage={item.highlight.defaultMessage} />
        )
      }
      category={
        <FormattedMessage id={item.category.id} defaultMessage={item.category.defaultMessage} />
      }
      title={<FormattedMessage id={item.title.id} defaultMessage={item.title.defaultMessage} />}
      date={
        item.date && (
          <FormattedMessage id={item.date.id} defaultMessage={item.date.defaultMessage} />
        )
      }
      venue={
        item.venue && (
          <FormattedMessage id={item.venue.id} defaultMessage={item.venue.defaultMessage} />
        )
      }
    />
  );

  // Layout mirrors the design grid as a horizontal carousel: the featured card spans
  // the full track height on the start (right) side, and the standard cards stack two
  // per column to its left.
  const featuredItem = EVENTS.find((e) => e.variant === 'featured');
  const standardItems = EVENTS.filter((e) => e.variant !== 'featured');
  const columns: EventItem[][] = [];
  for (let i = 0; i < standardItems.length; i += 2) columns.push(standardItems.slice(i, i + 2));

  // Figma card sizes. On mobile (base) the carousel flattens to a full-width vertical
  // stack (featured 423h, standard 264h); on md+ the featured spans the full track
  // height (720 = 2·350 + 20px stack gap) beside columns of two standard cards.
  const fullH = { base: '423px', md: '720px' };
  const halfH = { base: '264px', md: '350px' };
  const stackGap = { base: '16px', md: '20px' };

  return (
    <Box as='section' dir='rtl' bg='white' aria-labelledby='about-events-heading'>
      <VStack
        w='100%'
        maxW='container.xl'
        mx='auto'
        px={{ base: '20px', md: '100px' }}
        py={{ base: '48px', md: '80px' }}
        spacing={{ base: '24px', md: '40px' }}
        align='stretch'
      >
        <Flex align={{ base: 'flex-start', md: 'center' }} justify='space-between' gap='24px'>
          <SectionHeader
            titleId='about-events-heading'
            eyebrow={<FormattedMessage id='about.events.eyebrow' defaultMessage='Our events' />}
            eyebrowProps={{ fontWeight: '700', fontSize: { base: '15px', md: '16px' } }}
            title={
              <FormattedMessage id='about.events.title' defaultMessage='Moments to remember.' />
            }
            titleProps={{ fontWeight: '900', fontSize: { base: '30px', md: '44px' } }}
            subtitle={
              <FormattedMessage
                id='about.events.subtitle'
                defaultMessage='A selection of events and experiences that brought people together around music, sport and art.'
              />
            }
          />

          {/* Arrow controls — the keyboard affordance for scrolling. Placed on the end
              (left) side. In RTL the coral "next"/advance arrow points left and the
              outlined "prev" arrow points right. Order: prev (right), next (left). */}
          <Flex gap='12px' flexShrink={0} display={{ base: 'none', md: 'flex' }}>
            <CarouselArrow
              label={intl.formatMessage({ id: 'about.events.prev', defaultMessage: 'Previous' })}
              icon={ChevronRight}
              tone='outline'
              isDisabled={!canPrev}
              onClick={() => scrollByDir('prev')}
            />
            <CarouselArrow
              label={intl.formatMessage({ id: 'about.events.next', defaultMessage: 'Next' })}
              icon={ChevronLeft}
              tone='coral'
              isDisabled={!canNext}
              onClick={() => scrollByDir('next')}
            />
          </Flex>
        </Flex>

        <Flex
          ref={trackRef}
          dir='rtl'
          // base: a full-width vertical stack (the columns are already vertical, so the
          // whole grid flattens to one column). md+: the horizontal, swipeable carousel.
          direction={{ base: 'column', md: 'row' }}
          align={{ base: 'stretch', md: 'flex-start' }}
          gap={{ base: '16px', md: '20px' }}
          overflowX={{ base: 'visible', md: 'auto' }}
          overflowY={{ base: 'visible', md: 'hidden' }}
          pb={{ base: 0, md: '4px' }}
          // Smooth horizontal swipe on touch; hide the scrollbar for a clean track.
          sx={{
            scrollSnapType: 'x mandatory',
            WebkitOverflowScrolling: 'touch',
            scrollbarWidth: 'none',
            '&::-webkit-scrollbar': { display: 'none' },
          }}
        >
          {featuredItem && (
            <Box
              flex='0 0 auto'
              w={{ base: '100%', md: '415px' }}
              h={fullH}
              sx={{ scrollSnapAlign: 'start' }}
            >
              {renderCard(featuredItem)}
            </Box>
          )}
          {columns.map((col, ci) => (
            <Flex
              key={ci}
              direction='column'
              flex='0 0 auto'
              gap={stackGap}
              w={{ base: '100%', md: '294px' }}
              sx={{ scrollSnapAlign: 'start' }}
            >
              {col.map((item) => (
                <Box key={item.id} h={halfH}>
                  {renderCard(item)}
                </Box>
              ))}
            </Flex>
          ))}
        </Flex>
      </VStack>
    </Box>
  );
};

const CarouselArrow = ({
  label,
  icon,
  tone,
  isDisabled,
  onClick,
}: {
  label: string;
  icon: typeof ChevronLeft;
  tone: 'coral' | 'outline';
  isDisabled: boolean;
  onClick: () => void;
}) => {
  const coral = tone === 'coral';
  return (
    <IconButton
      aria-label={label}
      icon={<Box as={icon} boxSize='20px' strokeWidth={2.5} />}
      onClick={onClick}
      isDisabled={isDisabled}
      variant='unstyled'
      display='inline-flex'
      alignItems='center'
      justifyContent='center'
      boxSize='44px'
      minW='44px'
      borderRadius='9999px'
      border='1px solid'
      borderColor={coral ? '#FF5547' : '#E7E5E4'}
      bg={coral ? '#FF5547' : 'white'}
      color={coral ? 'white' : '#1B1917'}
      fontFamily={FONT_FAMILY}
      _hover={{ bg: coral ? '#F2563C' : '#FAFAF9' }}
      _disabled={{
        opacity: 0.4,
        cursor: 'not-allowed',
        _hover: { bg: coral ? '#FF5547' : 'white' },
      }}
    />
  );
};
